// Formatting + grouping helpers for the Veriff verification view (Potential Fraud section).

// "RESIDENCE_PERMIT" / "document-front" / "selfid_video" -> "Residence permit" style sentence case.
export const humanize = (value) => {
    if (value === null || value === undefined || value === "") return ""
    const text = String(value).replace(/[_-]+/g, " ").trim().toLowerCase()
    return text.charAt(0).toUpperCase() + text.slice(1)
}

export const formatDateTime = (date) => {
    if (!date) return ""
    const d = new Date(date)
    if (Number.isNaN(d.getTime())) return String(date)
    return d.toLocaleString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })
}

// Veriff dates are "YYYY-MM-DD" (no time) — format as "29 Oct 1997" without a timezone shift.
export const formatDate = (value) => {
    if (!value) return ""
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value))
    if (!match) return String(value)
    const d = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })
}

let regionNames
try {
    regionNames = new Intl.DisplayNames(["en"], { type: "region" })
}
catch (e) {
    regionNames = null
}

// "US" -> "United States"; anything else is returned as-is.
export const formatCountry = (code) => {
    if (!code) return ""
    if (regionNames && /^[A-Za-z]{2}$/.test(code)) {
        try {
            return regionNames.of(code.toUpperCase()) || code
        }
        catch (e) {
            return code
        }
    }
    return code
}

export const DECISION_STYLES = {
    approved: { label: "Approved", className: "border-green-200 bg-green-50 text-green-700" },
    declined: { label: "Declined", className: "border-red-200 bg-red-50 text-red-700" },
    resubmission_requested: { label: "Resubmission", className: "border-amber-200 bg-amber-50 text-amber-800" },
    submitted: { label: "Submitted", className: "border-blue-200 bg-blue-50 text-blue-700" },
    started: { label: "Started", className: "border-gray-200 bg-gray-50 text-gray-700" },
    created: { label: "Created", className: "border-gray-200 bg-gray-50 text-gray-700" },
    expired: { label: "Expired", className: "border-gray-200 bg-gray-100 text-gray-700" },
    abandoned: { label: "Abandoned", className: "border-gray-200 bg-gray-100 text-gray-700" },
}

export const statusStyle = (status) =>
    DECISION_STYLES[status] || { label: humanize(status) || "Unknown", className: "border-gray-200 bg-gray-50 text-gray-700" }

// Base context of a media file: "document-front-pre" -> "document-front" (Veriff's "-pre" suffix marks
// a preliminary capture of the same thing).
export const baseContext = (item) => String(item.context || item.name || item.mediaType || "other").replace(/-pre(?=-|$)/, "")

export const isPreliminary = (item) => /-pre(?=-|$)/.test(String(item.context || item.name || ""))

// Display name of a single file, e.g. "Document front (preliminary)".
export const mediaLabel = (item) => {
    const label = humanize(baseContext(item)) || "Media"
    return isPreliminary(item) ? `${label} (preliminary)` : label
}

// Attempt list for the view, newest first. Uses the stored Veriff attempts when available; otherwise
// derives "attempts" from the media's attempt ids (no status / time). The newest attempt's status is
// replaced by the session decision once decided (Veriff's attempt status stays "submitted").
// Media with no attempt id (from the latest-attempt fallback) belong to the newest attempt.
export const buildAttempts = (session) => {
    const media = session.media || []
    const decisionStatus = session.decision?.status

    let attempts
    if (Array.isArray(session.attempts) && session.attempts.length) {
        attempts = [...session.attempts]
            .sort((a, b) => new Date(b.createdTime || 0) - new Date(a.createdTime || 0))
            .map((attempt) => ({ ...attempt }))
    }
    else {
        const ids = [...new Set(media.map((item) => item.veriffAttemptId).filter(Boolean))]
        attempts = ids.map((id) => ({ id, status: null, createdTime: null, userDefinedData: [] }))
        if (!attempts.length && media.length) {
            attempts = [{ id: "latest", status: null, createdTime: null, userDefinedData: [] }]
        }
    }

    const newestId = attempts[0]?.id
    return attempts.map((attempt) => {
        const isNewest = attempt.id === newestId
        const displayStatus = isNewest && ["approved", "declined"].includes(decisionStatus)
            && (!attempt.status || attempt.status === "submitted")
            ? decisionStatus
            : attempt.status
        return {
            ...attempt,
            displayStatus,
            media: media.filter((item) => item.veriffAttemptId === attempt.id || (isNewest && !item.veriffAttemptId)),
        }
    })
}

// Group an attempt's media for the Veriff-style layout.
export const groupAttemptMedia = (media) => {
    const images = media.filter((item) => item.mediaType === "image")
    const byContext = (contexts) => images.filter((item) => contexts.includes(baseContext(item)))
    const documentContexts = ["document-front", "document-back"]
    const faceContexts = ["face"]
    return {
        documentFront: byContext(["document-front"]),
        documentBack: byContext(["document-back"]),
        face: byContext(faceContexts),
        otherImages: images.filter((item) => ![...documentContexts, ...faceContexts].includes(baseContext(item))),
        videos: media.filter((item) => item.mediaType === "video"),
        nfc: media.filter((item) => item.mediaType === "nfc"),
    }
}

// Value at a dotted path, or undefined.
export const pick = (object, path) => path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), object)

// Flatten a nested object into [ "a.b.c", value ] rows of its scalar leaves (arrays of scalars joined).
export const flattenObject = (object, prefix = "") => {
    if (object === null || object === undefined) return []
    if (typeof object !== "object") return [[prefix, object]]
    if (Array.isArray(object)) {
        if (object.every((value) => value === null || typeof value !== "object")) {
            return object.length ? [[prefix, object.join(", ")]] : []
        }
        return object.flatMap((value, index) => flattenObject(value, prefix ? `${prefix}.${index}` : String(index)))
    }
    return Object.entries(object).flatMap(([key, value]) => flattenObject(value, prefix ? `${prefix}.${key}` : key))
}
